import React from "react";
import { includes, map } from "lodash";
import Loading from "components/shared/Loading";
import classNames from "classnames";
import { LayoutRow, LayoutWidget } from "components/channels/types";
import WidgetContainer from "components/channels/widgets/WidgetContainer";
import { useWidget } from "components/channels/api";
import WidgetByType from "components/channels/widgets/WidgetByType";
import PageLoading from "components/shared/PageLoading";
import {
  getWidgetColspan,
  getWidgetContainerResponsiveHeight,
  getWidgetContentColumns,
} from "components/channels/helpers";
import { Translations } from "components/channels/ChannelPage";

export type LayoutData = {
  rows: LayoutRow[];
};

type ChannelLayoutArgs = {
  channelSlug?: string;
  layoutData: LayoutData | undefined;
  isLoading: boolean;
  channelTranslations?: Translations;
};

const W_LOOKUP = {
  full: [3],
  "half-1-2": [1, 2],
  "half-2-1": [2, 1],
  third: [1, 1, 1],
};

export function ChannelLayout({
  layoutData,
  isLoading,
  channelSlug,
  channelTranslations,
}: ChannelLayoutArgs) {
  if (isLoading || !layoutData) return <PageLoading />;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {map(layoutData.rows, (item) =>
        map(
          item.widgets as LayoutRow<typeof item.type>["widgets"],
          (widget: LayoutWidget | null, index) => (
            <Widget
              key={index}
              w={W_LOOKUP[item.type][index]}
              channelSlug={channelSlug}
              widgetId={widget?.id}
              channelTranslations={channelTranslations}
            />
          ),
        ),
      )}
    </div>
  );
}

function Widget({
  widgetId,
  w,
  channelSlug,
  channelTranslations,
}: {
  channelSlug?: string;
  widgetId?: string;
  w: number;
  channelTranslations?: Translations;
}) {
  const { data: widget, isLoading } = useWidget({ channelSlug, widgetId });
  const widgetTranslations =
    widgetId && channelTranslations
      ? getWidgetTranslations(widgetId, channelTranslations)
      : undefined;

  if (isLoading) {
    return (
      <div className={`animate-pulse ${getWidgetColspan(w)}`}>
        <div
          className={classNames(
            `
            bg-gray-300 rounded
            flex justify-center items-center
            col-span-1
          `,
            getWidgetContainerResponsiveHeight(w),
          )}
        >
          <Loading />
        </div>
      </div>
    );
  }

  const localizedWidget =
    widget && widgetTranslations
      ? {
          ...widget,
          ...widgetTranslations,
        }
      : widget;
  return localizedWidget ? (
    <WidgetContainer
      w={w}
      id={widget?.id}
      title={localizedWidget.title}
      titleBackgroundOverride={localizedWidget.title_background_override}
      collapseToSingleColumn={includes(
        ["blog", "spotlight", "banner", "link"],
        localizedWidget.type,
      )}
      singleRowOnly={includes(["appointments"], localizedWidget.type)}
      label={localizedWidget.label}
      hideTitle={
        localizedWidget.hide_title || localizedWidget.type === "banner"
      }
    >
      <WidgetByType {...localizedWidget} />
    </WidgetContainer>
  ) : (
    <div
      className={classNames(
        "flex",
        getWidgetContainerResponsiveHeight(w),
        getWidgetContentColumns(w),
      )}
    />
  );
}

function getWidgetTranslations(
  widgetId: string,
  channelTranslations: Translations,
) {
  const prefix = `widget_${widgetId}_`;
  const widgetTranslations: Record<string, string> = {};

  if (channelTranslations.title)
    widgetTranslations.title = channelTranslations.title;
  if (channelTranslations.description)
    widgetTranslations.description = channelTranslations.description;

  Object.keys(channelTranslations).forEach((key) => {
    if (key.startsWith(prefix)) {
      const fieldName = key.substring(prefix.length);
      widgetTranslations[fieldName] = channelTranslations[key];
    }
  });

  return widgetTranslations;
}
